/**
 * @file sounds.es6
 * @desc Sounds load and usage based on Howler.
 * @copyright 2016 (c) KSI Corporation
 * */

// depends on global Howler

export default class Sounds
{
    constructor() {
        this.sounds = []
    }

    /**
     * Load sounds one-by-one.
     * @param resources Array of paths to sounds needs to be loaded, without extensions.
     * @param onLoad Invoked when everything loaded.
     * @param onProgress Invoked on load progress, [ 0, 1 ].
     */
    load( resources, on_load, on_progress, urlModifier )
    {
        urlModifier = urlModifier || ( url => url )
        var soundIndex = 0;
        var reported = false;
        var loadedSounds = 0;

        const loadSound = () =>
        {
            if ( soundIndex >= resources.length )
            {
                if ( loadedSounds >= resources.length && reported == false )
                {
                    reported = true;
                    on_load();
                }
            }
            else
            {
                var soundName = resources[ soundIndex ];
                ++ soundIndex;

                // console.log( "Loading sound " + soundName + " ..." );

                var howl_sound = new Howl( {
                    src : [
                        // soundName + ".m4a",
                        urlModifier( soundName + ".mp3" )
                    ],
                    preload : true,
                    onload : () =>
                    {
                        ++ loadedSounds;
                        
                        on_progress( loadedSounds / resources.length );
                        
                        //replicating fake sound to loaded one:
                        const fake = this.sounds[ soundName ]
                        if ( fake ) replicate_fake( fake, howl_sound )
                        this.sounds[ soundName ] = howl_sound;

                        loadSound();
                    },
                    onloaderror : ( ID, message ) => {
                        console.error( "Sound \"" + soundName + "\":" + ID + " loading failed: " + message );

                        //у нас политика игнорирования звуков НЕмогущих загрузиться:
                        ++ loadedSounds;
                        
                        loadSound();
                    }
                } )
            }
        }

        //просто загрузка параллельно:
        for ( var i = 0; i < 10; ++ i )
        {
            loadSound();
        }
    }

    play( name )
    {
        const sound = this.sounds[ name ];
        if ( sound )
        {
            sound.play();
        }
        else
        {
            //no need to do anything if sounds not loaded yet ...
        }
    }

    get( name )
    {
        const sound = this.sounds[ name ];
        if ( sound )
        {
            return sound;
        }
        const fake = new FakeSound;
        this.sounds[ name ] = fake;
        return fake;
    }

    muteAll(muted) {
        
        for (let s in this.sounds) {
            this.sounds[s].mute(muted);
        }
    }
}

sounds = new Sounds

function replicate_fake( fake, howl )
{
    fake.real = howl

    for ( const c of fake._on ) howl.on( c.name, c.callback )
    delete fake._on

    for ( const c of fake._once ) howl.once( c.name, c.callback )
    delete fake._once

    if ( fake._loop ) howl.loop( fake._loop )

    if ( fake.playing_timeout ) {
        clearTimeout( fake.playing_timeout )
        delete fake.playing_timeout
        howl.play()
    }
    if ( fake.fading_timeout ) {
        clearTimeout( fake.fading_timeout )
        delete fake.fading_timeout
        howl.fade( fake._fade_from, fake._fade_to, fake._fade_duration )
    }
    delete fake._fade_from
    delete fake._fade_to
    delete fake._fade_duration

    if ( fake.hasOwnProperty( '_volume' ) ) {
        howl.volume( fake._volume )
        delete fake._volume
    }

    if ( fake.hasOwnProperty( '_rate' ) ) {
        howl.rate( fake._rate )
        delete fake._rate
    }
    
    if ( fake.should_be_playing ) {
        howl.play()
        delete fake.should_be_playing
    }

    if ( fake.hasOwnProperty( '_muted' ) ) {
        howl.mute( fake._muted )
        delete fake._muted
    }
}

class FakeSound
{
    constructor()
    {
        this._on = []
        this._once = []
    }

    off()
    {
        if ( this.real ) return this.real.off.apply( this.real, arguments )

        this._on = []
        this._once = []
    }

    volume( volume )
    {
        if ( this.real ) return this.real.volume.apply( this.real, arguments )
        this._volume = volume
    }

    rate( rate )
    {
        if ( this.real ) return this.real.rate.apply( this.real, arguments )
        this._rate = rate
    }

    fade( from, to, duration )
    {
        if ( this.real ) return this.real.fade.apply( this.real, arguments )

        this._fade_from = from
        this._fade_to = to
        this._fade_duration = duration

        this.fading_timeout = setTimeout(
            () => {
                delete this.fading_timeout
                for ( let c of this._on ) if ( c.name == 'fade' ) c.callback()
                this._invoke_once( 'fade' )
            },
            duration
        )
    }

    play()
    {
        if ( this.real ) return this.real.play.apply( this.real, arguments );
        
        if ( this.playing() ) return

        //stop later on if not on a loop:
        if ( ! this._loop ) this._enque_stop();
        
        if ( this._loop ) this.should_be_playing = true
    }
    _enque_stop()
    {
        this.playing_timeout = setTimeout(
            () => this.on_end(),
            1000
        );
    }

    on_end()
    {
        delete this.playing_timeout;

        for ( const c of this._on ) if ( c.name == 'end' ) c.callback()

        this._invoke_once( 'end' )
    }

    _invoke_once( name )
    {
        for ( let i = this._once.length - 1; i >= 0; -- i ) {
            const c = this._once[ i ]
            if ( c.name == name ) {
                this._once.splice( i, 1 )
                c.callback()
            }
        }
    }
    
    stop()
    {
        if ( this.real ) return this.real.stop.apply( this.real, arguments );

        if ( this.playing_timeout ) {
            clearTimeout( this.playing_timeout );
            delete this.playing_timeout;
        }

        this.on_end();
        
        delete this.should_be_playing
    }

    pause() {
        if ( this.real ) return this.real.stop.apply( this.real, arguments );
    }

    playing()
    {
        if ( this.real ) return this.real.playing.apply( this.real, arguments );
        return this.playing_timeout || this.fading_timeout
    }
    
    on( name, callback )
    {
        if ( this.real ) return this.real.on.apply( this.real, arguments );

        this._on.push( {
            name,
            callback,
        } )
    }
    
    once( name, callback )
    {
        if ( this.real ) return this.real.once.apply( this.real, arguments );

        this._once.push( {
            name,
            callback,
        } )
    }

    loop( v )
    {
        if ( this.real ) return this.real.loop.apply( this.real, arguments );

        this._loop = v;

        if ( this.playing() && ! v ) this._enque_stop();
    }

    duration()
    {
        if ( this.real ) return this.real.duration.apply( this.real, arguments );

        return 1;
    }

    mute(muted) {
        this._muted = muted;
    }
}



// WEBPACK FOOTER //
// ./src/library/sounds.es6